import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CmsPersonalDetailsPage,
  PersonalDetailsForm,
  PersonalDetailsFormConfig,
  PersonalDetailsFormValue,
} from '@checkout/util-models';
import { BasketFacade, QuoteDetails } from '@domgen/dgx-fe-basket';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  ClientBrandCode,
  CustomerDetails,
  TitleId,
  filterNullUndefined,
} from '@domgen/dgx-fe-common';
import { titleIdOptions } from '@domgen/dgx-fe-forms/select';
import {
  FORM_CONFIG,
  FormConfig,
  FormOption,
} from '@domgen/dgx-fe-forms/shared';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, of } from 'rxjs';
import { first, map, mergeMap, tap } from 'rxjs/operators';
import { formatCustomerDetailsToPersonalDetailsFormValue } from '../../utils/personal-details-form/personal-details-form.utils';
import { PersonalDetailsFormBuilder } from '../personal-details-form-builder/personal-details-form-builder.service';

export interface ViewModel {
  applianceBrandCodes: string[];
  cmsData: CmsPersonalDetailsPage;
  form?: FormGroup<PersonalDetailsForm>;
  selectedQuotes?: QuoteDetails[];
  showRequiredFieldLegend: boolean;
  titleIdOptions: FormOption<TitleId>[];
}

export interface ComponentState {
  applianceGroupCodes: string[];
  form?: FormGroup<PersonalDetailsForm>;
}

@Injectable()
export class PersonalDetailsFormComponentService extends ComponentStore<ComponentState> {
  private readonly applianceBrandCodes$: Observable<string[]> =
    this.basketFacade.selectedQuotes$.pipe(
      map((quotes: QuoteDetails[]): string[] =>
        quotes.reduce(
          (brandCodes: string[], quote: QuoteDetails) =>
            brandCodes.includes(quote.brandCode)
              ? brandCodes
              : [...brandCodes, quote.brandCode],
          [ClientBrandCode.DomesticAndGeneral]
        )
      )
    );

  private readonly formInitialValue$: Observable<
    PersonalDetailsFormValue | undefined
  > = this.checkoutFacade.customerDetails$.pipe(
    map((customerDetails: CustomerDetails | undefined) =>
      formatCustomerDetailsToPersonalDetailsFormValue(customerDetails)
    )
  );

  private readonly formConfig$: Observable<PersonalDetailsFormConfig> =
    combineLatest({
      cmsData: this.cmsContainer.content$,
      initialValue: this.formInitialValue$,
    });

  readonly form$: Observable<FormGroup<PersonalDetailsForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  readonly vm$: Observable<ViewModel> = combineLatest({
    applianceBrandCodes: this.applianceBrandCodes$,
    cmsData: this.cmsContainer.content$,
    form: this.form$,
    selectedQuotes: this.basketFacade.selectedQuotes$,
    showRequiredFieldLegend: of(Boolean(!this.formConfig.requiredHidden)),
    titleIdOptions: of(titleIdOptions),
  });

  constructor(
    @Inject(FORM_CONFIG) private readonly formConfig: FormConfig,
    private readonly basketFacade: BasketFacade,
    private readonly cmsContainer: CmsContainer<CmsPersonalDetailsPage>,
    private readonly personalDetailsFormBuilder: PersonalDetailsFormBuilder,
    private readonly checkoutFacade: CheckoutFacade
  ) {
    super({
      applianceGroupCodes: [],
    });
  }

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<PersonalDetailsForm>) => ({
      ...state,
      form,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: PersonalDetailsFormConfig) =>
        this.personalDetailsFormBuilder.build(config)
      ),
      tap((form) => this.updateForm(form))
    )
  );
}
